import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { PRESETS } from "../../hooks/useFlowAnalysisFilters";
import "../../style/mfca-dashboard.css";

const PRESET_OPTIONS = [
  { key: PRESETS.LAST_30, label: "Last 30 days" },
  { key: PRESETS.LAST_90, label: "Last 90 days" },
  { key: PRESETS.YTD, label: "Year to date" },
  { key: PRESETS.PREV_QUARTER, label: "Previous quarter" },
  { key: PRESETS.CUSTOM, label: "Custom" },
];

const parseDate = (str) => {
  if (!str) return null;
  // YYYY-MM-DD parsed as local date, not utc, to avoid off-by-one
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const fmtDate = (d) => d.toISOString().slice(0, 10);

// human-readable period stamp shown above the dashboard. exported so the parent
// page can render it wherever it likes (we no longer render it inline here so
// the period chips can sit alongside the LastUpdatedBadge).
export const formatPeriodStamp = (dateFrom, dateTo, locale) => {
  if (!dateFrom || !dateTo) return "";
  const from = parseDate(dateFrom);
  const to = parseDate(dateTo);
  const opts = { year: "numeric", month: "short", day: "numeric" };
  return `${from.toLocaleDateString(locale, opts)} – ${to.toLocaleDateString(locale, opts)}`;
};

// friendly duration alongside the period stamp. takes an i18n t() so the
// "month / day / year" words translate. computes calendar-aware months
// rather than 30-day approximations so a Year-to-date or quarter range
// reads naturally ("3 months, 27 days" rather than "118 days"). plural
// branches inline rather than via i18next plural keys to keep the locale
// strings explicit and easy to translate.
export const formatPeriodDuration = (dateFrom, dateTo, t) => {
  if (!dateFrom || !dateTo) return "";
  const from = parseDate(dateFrom);
  const to = parseDate(dateTo);
  if (!from || !to || to < from) return "";

  const msPerDay = 1000 * 60 * 60 * 24;
  const totalDays = Math.round((to - from) / msPerDay) + 1;
  if (totalDays <= 0) return "";
  if (totalDays === 1) return t("1 day");
  if (totalDays < 31) return t("{{n}} days", { n: totalDays });

  // calendar-aware year/month/day breakdown for longer ranges
  let years = to.getFullYear() - from.getFullYear();
  let months = to.getMonth() - from.getMonth();
  let days = to.getDate() - from.getDate();
  if (days < 0) {
    months -= 1;
    const prev = new Date(to.getFullYear(), to.getMonth(), 0);
    days += prev.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const parts = [];
  if (years > 0) {
    parts.push(years === 1 ? t("1 year") : t("{{n}} years", { n: years }));
  }
  if (months > 0) {
    parts.push(months === 1 ? t("1 month") : t("{{n}} months", { n: months }));
  }
  if (days > 0 && years === 0) {
    parts.push(days === 1 ? t("1 day") : t("{{n}} days", { n: days }));
  }
  return parts.join(", ");
};

const PeriodSelector = ({ preset, dateFrom, dateTo, onPresetChange, onFromChange, onToChange }) => {
  const { t } = useTranslation();
  const showCustom = preset === PRESETS.CUSTOM;

  return (
    <div className="rl-period">
      <div className="rl-period-presets" role="tablist" aria-label={t("Period")}>
        {PRESET_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            type="button"
            role="tab"
            aria-selected={preset === opt.key}
            className={`rl-period-preset ${preset === opt.key ? "is-active" : ""}`}
            onClick={() => onPresetChange(opt.key)}
          >
            {t(opt.label)}
          </button>
        ))}
      </div>

      {showCustom && (
        <div className="rl-period-custom">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t("From")}
              value={parseDate(dateFrom)}
              onChange={(d) => d && onFromChange(fmtDate(d))}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label={t("To")}
              value={parseDate(dateTo)}
              onChange={(d) => d && onToChange(fmtDate(d))}
              slotProps={{ textField: { size: "small" } }}
            />
          </LocalizationProvider>
        </div>
      )}
    </div>
  );
};

PeriodSelector.propTypes = {
  preset: PropTypes.string.isRequired,
  dateFrom: PropTypes.string,
  dateTo: PropTypes.string,
  onPresetChange: PropTypes.func.isRequired,
  onFromChange: PropTypes.func.isRequired,
  onToChange: PropTypes.func.isRequired,
};

export default PeriodSelector;
